import {
  DataGrid,
  formatDate,
  StatusBadge,
  type GridQuery
} from '@ijoin/components';
import {
  GetEnrollmentSubmissionLogParams,
  SearchSubmissionLogRes,
  useGetEnrollmentSubmissionLog
} from '@ijoin/hooks';
import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography
} from '@mui/material';

import { FC, useState } from 'react';

import { QueryBoundary } from './QueryBoundary';
import { SubmissionDetailsTabs } from './SubmissionDetailsTabs';

export const EnrollmentSubmissionLogSection: FC<{
  planEnrollmentId: string;
  planId: string;
}> = props => {
  const [query, setQuery] = useState<GridQuery>();
  const [selected, setSelected] = useState<null | SearchSubmissionLogRes>(null);

  const submissions = useGetEnrollmentSubmissionLog(
    props.planId,
    props.planEnrollmentId,
    query && {
      dir: query.dir as GetEnrollmentSubmissionLogParams['dir'],
      skip: query.skip,
      sort: query.sort as GetEnrollmentSubmissionLogParams['sort'],
      take: query.take
    },
    { query: { enabled: !!query, placeholderData: prev => prev } }
  );

  return (
    <Card sx={{ p: 1 }}>
      <CardHeader title='Submission Log' />
      <CardContent>
        <DataGrid
          columns={[
            {
              field: 'id',
              flex: 1,
              headerName: 'ID',
              renderCell: params => (
                <Button
                  onClick={() => setSelected(params.row)}
                  sx={{ textTransform: 'none' }}
                  variant='text'>
                  {params.value}
                </Button>
              ),
              sortable: false
            },
            {
              field: 'queueDateTime',
              flex: 1,
              headerName: 'Queued At',
              valueFormatter: value =>
                value
                  ? formatDate(value, { format: 'MM/DD/YYYY h:mm:ss A' })
                  : ''
            },
            {
              field: 'scheduledReleaseDateTime',
              flex: 1,
              headerName: 'Scheduled Release',
              valueFormatter: value =>
                value
                  ? formatDate(value, { format: 'MM/DD/YYYY h:mm:ss A' })
                  : ''
            },
            {
              field: 'processedDateTime',
              flex: 1,
              headerName: 'Processed At',
              valueFormatter: value =>
                value
                  ? formatDate(value, { format: 'MM/DD/YYYY h:mm:ss A' })
                  : ''
            },
            {
              field: 'jobStatus',
              flex: 0.75,
              headerName: 'Job Status',
              renderCell: ({ value }) => {
                const v = value?.toLowerCase() ?? '';
                return value ? (
                  <StatusBadge
                    severity={
                      v.includes('completed') || v.includes('success')
                        ? 'success'
                        : v.includes('error') || v.includes('fail')
                          ? 'error'
                          : v.includes('warning')
                            ? 'warning'
                            : 'info'
                    }>
                    {value}
                  </StatusBadge>
                ) : (
                  ''
                );
              }
            },
            { field: 'systemTask', flex: 1, headerName: 'System Task' }
          ]}
          disableColumnMenu
          disableRowSelectionOnClick
          getRowId={row => row.id}
          initialSort={[{ field: 'queueDateTime', sort: 'desc' }]}
          loading={!query || submissions.isFetching}
          onSearch={setQuery}
          pageSize={25}
          pageSizeOptions={[10, 25, 50, 100]}
          paginationMode='server'
          rowCount={submissions.data?.pagination?.total ?? 0}
          rows={submissions.data?.data ?? []}
          showColumnMenu
          slotProps={{
            root: {
              'data-component': 'dataGrid',
              'data-testid': 'enrollmentSubmissionLog'
            }
          }}
          sortingMode='server'
        />

        <Dialog
          fullWidth
          maxWidth='md'
          onClose={() => setSelected(null)}
          open={!!selected}>
          <DialogTitle
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'space-between'
            }}>
            <Typography variant='h4'>{selected?.id}</Typography>
            <IconButton onClick={() => setSelected(null)}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {selected && (
              <QueryBoundary>
                <SubmissionDetailsTabs
                  recordKeeperExportQueueId={selected.id}
                />
              </QueryBoundary>
            )}
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

EnrollmentSubmissionLogSection.displayName = 'EnrollmentSubmissionLogSection';
